/**
 * 화면 어디서나 쓰는 스크린리더 안내기.
 *
 * 간편모드(DotEasyCanvas·EasyPresentation)와 발표자 보기에서 검증한 방식을 한 곳에 모았다.
 *  - 이미 접근성 트리에 있는 라이브 영역 두 개를 번갈아 채운다. Vue 텍스트 바인딩만 바꾸면
 *    NVDA/브라우저 조합에 따라 변경을 놓치고, 한 영역에 같은 문장을 다시 쓰면 읽지 않는다.
 *  - 비운 뒤 80ms 있다가 채운다. 같은 틱에 비웠다 채우면 브라우저가 두 변경을 하나로 합쳐 아무것도 읽지 않는다.
 *  - 대화상자(aria-modal)가 열려 있으면 그 안의 영역에서 읽는다. 배경은 a11yModal 이 aria-hidden 으로
 *    감추고, 브라우저도 aria-modal 바깥을 무시할 수 있어 body 에 둔 영역은 그동안 들리지 않는다.
 *  - 영역은 미리 만들어 둔다(prepareLiveRegions). 막 생긴 라이브 영역의 첫 변경은 읽지 않는 경우가 있다.
 */

export const LIVE_REGION_ATTR = "data-a11y-live"
const ANNOUNCE_DELAY = 80
// 방금 만든 영역은 접근성 트리에 자리 잡을 시간을 조금 더 준다.
const FRESH_REGION_DELAY = 250

const OFFSCREEN_STYLE = {
    position: "absolute",
    width: "1px",
    height: "1px",
    margin: "-1px",
    padding: "0",
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    whiteSpace: "nowrap",
    border: "0",
}

const stateByHost = new WeakMap()
let timer = null

function createRegion(politeness) {
    const region = document.createElement("div")
    // polite 는 status(차례가 오면 읽음), assertive 는 role 없이 aria-live 만 둔다.
    // role="alert" 를 붙이면 스크린리더에 따라 "알림" 을 앞에 붙여 읽는다.
    if (politeness === "polite") region.setAttribute("role", "status")
    region.setAttribute("aria-live", politeness)
    region.setAttribute("aria-atomic", "true")
    return region
}

/** host 안에 안내 영역을 만들어 둔다(이미 있으면 그대로). 대화상자는 열리기 직전에 부른다. */
export function prepareLiveRegions(host = document.body) {
    if (!host) return null
    let state = stateByHost.get(host)
    if (state && state.box.isConnected && state.box.parentElement === host) return state

    const box = document.createElement("div")
    box.setAttribute(LIVE_REGION_ATTR, "")
    Object.assign(box.style, OFFSCREEN_STYLE)
    state = {
        box,
        createdAt: Date.now(),
        polite: [createRegion("polite"), createRegion("polite")],
        assertive: [createRegion("assertive"), createRegion("assertive")],
        next: {polite: 0, assertive: 0},
    }
    box.append(...state.polite, ...state.assertive)
    host.appendChild(box)
    stateByHost.set(host, state)
    return state
}

function isVisible(el) {
    if (!el || !el.isConnected) return false
    if (el.getAttribute("aria-hidden") === "true") return false
    const style = el.ownerDocument.defaultView?.getComputedStyle?.(el)
    if (style && (style.display === "none" || style.visibility === "hidden")) return false
    return true
}

/** 지금 안내를 받아야 할 곳: 포커스를 가진 대화상자 > 마지막으로 열린 대화상자 > body */
export function findAnnounceHost() {
    const dialogs = Array.from(document.querySelectorAll('[aria-modal="true"]')).filter(isVisible)
    if (!dialogs.length) return document.body
    const active = document.activeElement
    return dialogs.find(d => d.contains(active)) || dialogs[dialogs.length - 1]
}

/**
 * 안내 문장을 읽힌다.
 * @param {string} message
 * @param {{politeness?: "polite"|"assertive", host?: HTMLElement}} [options]
 *   assertive 는 오류처럼 지금 하던 낭독을 끊고 읽어야 할 때만.
 */
export function announce(message, {politeness = "polite", host} = {}) {
    const text = String(message ?? "").replace(/\s+/g, " ").trim()
    if (!text || typeof document === "undefined") return

    const target = host || findAnnounceHost()
    const state = prepareLiveRegions(target)
    if (!state) return

    if (timer) clearTimeout(timer)
    // 먼저 모두 비워 이전 안내와 분리한다.
    ;[...state.polite, ...state.assertive].forEach(region => { region.textContent = "" })

    const fresh = Date.now() - state.createdAt < FRESH_REGION_DELAY
    timer = setTimeout(() => {
        timer = null
        const pair = politeness === "assertive" ? state.assertive : state.polite
        const index = state.next[politeness === "assertive" ? "assertive" : "polite"]
        state.next[politeness === "assertive" ? "assertive" : "polite"] = index === 0 ? 1 : 0
        pair[index].textContent = text
    }, fresh ? FRESH_REGION_DELAY : ANNOUNCE_DELAY)
}

/** 아직 읽지 않은 안내를 버린다(화면을 떠날 때). */
export function cancelAnnouncement() {
    if (timer) {
        clearTimeout(timer)
        timer = null
    }
}
